import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';

import { useCurrentUser } from '../../contexts/CurrentUser';
import {
  getLoadedSudo,
  restoreLoadedSudo,
  useSharedSudo,
  useSudoDialogRequests,
  useSudoStale,
  watchSudoState,
} from '../../utils/sudoState';
import '../Modal/modal.scss';
import styles from './WorkspaceChangeGuard.module.scss';

// Workspace names are rendered by React, which escapes them itself; i18next's
// own escaping would turn "UK & Europe" into "UK &amp; Europe".
const NO_ESCAPE = { interpolation: { escapeValue: false } };

const SWITCHED_KEY =
  'You switched to <strong>{{workspace}}</strong> in another tab.';
const UNAVAILABLE_KEY =
  'Some actions may be unavailable until you switch to <strong>{{workspace}}</strong>.';

/**
 * Names for the two sides of a stale tab.
 *
 * `loaded` is the workspace this page rendered; `shared` is the one the user
 * switched to in another tab, and the one every request now goes to. Under
 * sudo /users/me describes the sudo workspace, so the user's own workspace
 * name is only known to a tab that loaded without sudo; when the *other* tab
 * left sudo, its workspace is the user's own and has no name here.
 */
export function useSudoWorkspaceNames() {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const shared = useSharedSudo();

  const mainWorkspace = String(t('Main workspace'));
  const loadedName: string =
    getLoadedSudo().name ?? currentUser?.workspace?.name ?? mainWorkspace;
  const sharedName: string = shared.name ?? mainWorkspace;

  return { loadedName, sharedName, shared };
}

/**
 * Warns when sudo was switched in another tab. See utils/sudoState.ts for why
 * that leaves this tab broken, and why the tab is read-only until the user
 * brings its workspace back.
 *
 * A dialog, so the user learns about it before the next request fails. If
 * they choose to stay, the dialog goes away and nothing else is shown: the
 * sudo banner in the Layout header already names the workspace the user is in
 * now, and a blocked write brings the dialog back.
 */
export function WorkspaceChangeGuard() {
  const { t } = useTranslation();
  const stale = useSudoStale();
  const dialogRequests = useSudoDialogRequests();
  const { loadedName, sharedName } = useSudoWorkspaceNames();
  const [dismissed, setDismissed] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const stayRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  useEffect(() => watchSudoState(), []);

  // The other tab may switch back, or switch again: each divergence deserves
  // its own dialog, and a blocked write reopens it on demand.
  useEffect(() => {
    if (!stale) setDismissed(false);
  }, [stale]);
  useEffect(() => {
    if (dialogRequests > 0) setDismissed(false);
  }, [dialogRequests]);

  const dialogOpen = stale && !dismissed;

  useEffect(() => {
    if (!dialogOpen) return;

    const opener = document.activeElement;
    openerRef.current = opener instanceof HTMLElement ? opener : null;
    // The least destructive action gets focus: "Switch" reloads the page.
    stayRef.current?.focus();

    return () => {
      const target = openerRef.current;
      openerRef.current = null;
      if (target && document.contains(target)) target.focus();
    };
  }, [dialogOpen]);

  useEffect(() => {
    if (!dialogOpen) return;

    // Keep Tab / Shift+Tab inside the dialog: the page behind it is read-only
    // anyway, and a screen-reader user should not wander into it.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setDismissed(true);
        return;
      }
      if (event.key !== 'Tab') return;

      const buttons = Array.from(
        dialogRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? [],
      );
      if (buttons.length === 0) return;

      const first = buttons[0];
      const last = buttons[buttons.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [dialogOpen]);

  if (!dialogOpen) return null;

  const modalRoot = document.getElementById('modal-root') ?? document.body;
  const dismiss = () => setDismissed(true);

  return ReactDOM.createPortal(
    <div className={styles.backdrop}>
      <div
        className={classNames('modal-dialog', styles.dialog)}
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="workspace-change-title"
        aria-describedby="workspace-change-description"
      >
        <div className="modal-header">
          <h2 className="modal-title" id="workspace-change-title">
            {t('This page is from another workspace')}
          </h2>
          <button
            type="button"
            className={styles.close}
            aria-label={t('Close')}
            onClick={dismiss}
          >
            &times;
          </button>
        </div>
        <div className="modal-body">
          <div
            className={classNames('modal-content', styles.body)}
            id="workspace-change-description"
          >
            <p>
              <Trans
                i18nKey={SWITCHED_KEY}
                defaults={SWITCHED_KEY}
                values={{ workspace: sharedName }}
                tOptions={NO_ESCAPE}
              />
            </p>
            <p>
              <Trans
                i18nKey={UNAVAILABLE_KEY}
                defaults={UNAVAILABLE_KEY}
                values={{ workspace: loadedName }}
                tOptions={NO_ESCAPE}
              />
            </p>
          </div>
        </div>
        <div className={classNames('modal-footer', styles.footer)}>
          <button
            type="button"
            className="secondary"
            ref={stayRef}
            title={sharedName}
            onClick={dismiss}
          >
            <span className={styles.truncate}>
              {t('Stay in {{workspace}}', {
                workspace: sharedName,
                ...NO_ESCAPE,
              })}
            </span>
          </button>
          <button
            type="button"
            className="btn-primary"
            title={loadedName}
            onClick={restoreLoadedSudo}
          >
            <span className={styles.truncate}>
              {t('Switch to {{workspace}}', {
                workspace: loadedName,
                ...NO_ESCAPE,
              })}
            </span>
          </button>
        </div>
      </div>
    </div>,
    modalRoot,
  );
}
